import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiX } from "react-icons/fi";
import { useServiceabilityLocationTree } from "./hooks/useServiceability";
import type { CountryLocationGroup, ServiceabilityLocation } from "./types";
import { ServiceabilityNode } from "./components/ServiceabilityNode";
import { FormLabel } from "newui/components/ui/FormLabel";
import { FormikFieldError } from "newui/components/ui/FormikFieldError/FormikFieldError";
import {
	baseInputClasses,
	errorInputClasses,
	normalInputClasses,
	getInputWrapperClasses,
} from "newui/components/ui/InputWithLabel/inputStyles";
import InfoTooltip from "newui/components/InfoTooltip";
import { buildLocationMap, buildDisplayChips, getAllDescendantIds } from "./serviceability-utils";

export interface ServiceabilityLocationSelectorProps {
	name?: string;
	errorText?: string;
	hasError?: boolean;
	label?: string;
	placeholder?: string;
	required?: boolean;
	value: number[];
	onChange: (ids: number[]) => void;
}

export const ServiceabilityLocationSelector: React.FC<ServiceabilityLocationSelectorProps> = ({
	errorText,
	hasError = false,
	label = "Serviceability Locations",
	name,
	onChange,
	placeholder = "Select locations you can service",
	required,
	value,
}) => {
	const { values: tree, isLoading } = useServiceabilityLocationTree();
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const selectedIds = useMemo(() => new Set(value), [value]);
	const hasInputError = !!errorText || hasError;

	const locationMap = useMemo(() => buildLocationMap(tree), [tree]);

	const displayChips = useMemo(
		() => buildDisplayChips(selectedIds, tree),
		[selectedIds, tree],
	);

	const toggle = useCallback(
		(id: number, descendantIds: number[]) => {
			const next = new Set(selectedIds);
			const allSelected = descendantIds.every((i) => next.has(i));

			if (allSelected) {
				descendantIds.forEach((i) => next.delete(i));
			} else {
				descendantIds.forEach((i) => next.add(i));
			}

			onChange(Array.from(next));
		},
		[selectedIds, onChange],
	);

	const removeChip = (chipId: number) => {
		const loc = locationMap.get(chipId);
		if (!loc) return;

		const idsToRemove = new Set(getAllDescendantIds(loc));
		onChange(value.filter((v) => !idsToRemove.has(v)));
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	return (
		<div className={getInputWrapperClasses(hasInputError)} ref={containerRef}>
			{label && (
				<FormLabel htmlFor={name} label={label} required={required}>
					<div className="inline-block">
						<InfoTooltip tooltipText="The locations you are able to provide services" />
					</div>
				</FormLabel>
			)}

			<div className="relative">
				<div
					className={`
						flex cursor-pointer items-center py-4
						${baseInputClasses}
						${hasInputError ? errorInputClasses : normalInputClasses}
						${open ? (hasInputError ? "border-red-400 ring-2 ring-red-100" : "border-blue-400 ring-2 ring-blue-100") : ""}
					`}
					onClick={() => !isLoading && setOpen(!open)}
				>
					<div className="flex flex-1 flex-wrap gap-1">
						{isLoading ? (
							<span className="text-gray-400">Loading...</span>
						) : displayChips.length === 0 ? (
							<span className="text-gray-400">{placeholder}</span>
						) : (
							displayChips.map((chip) => (
								<span
									key={chip.id}
									className="flex items-center rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-sm text-gray-700"
								>
									{chip.label}
									<FiX
										className="ml-1 cursor-pointer text-gray-400 hover:text-red-500"
										size={14}
										onClick={(e) => {
											e.stopPropagation();
											removeChip(chip.id);
										}}
									/>
								</span>
							))
						)}
					</div>

					<FiChevronDown
						className={`ml-2 shrink-0 text-blue-500 transition-transform ${open ? "rotate-180" : ""}`}
						size={20}
					/>
				</div>

				{open && (
					<div className="absolute z-10 mt-1 max-h-[300px] w-full overflow-y-auto rounded-md border border-gray-200 bg-white p-3 shadow-lg">
						{tree.map((countryGroup: CountryLocationGroup) => (
							<div key={countryGroup.country.id} className="mb-2 last:mb-0">
								<h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
									{countryGroup.country.name}
								</h4>

								{countryGroup.locations.map(
									(node: ServiceabilityLocation) => (
										<ServiceabilityNode
											key={node.id}
											node={node}
											selectedIds={selectedIds}
											onToggle={toggle}
											level={0}
										/>
									),
								)}
							</div>
						))}
					</div>
				)}
			</div>

			<FormikFieldError text={errorText} />
		</div>
	);
};
